import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ArrowUpward, ArrowDownward } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';
import debounce from 'lodash/debounce';

import { getChartProps, getErrorScreenParams } from './utils';
import LineChart from '../../../common/components/charts/LineChart';
import * as constants from '../../../constants/constant';
import DownloadTrend from '../../Download/DownloadTrend';
import ToggleFilter from '../../Filter/ToggleFilter';
import ErrorScreenMessage from '../../ErrorScreenMessage/ErrorScreenMessage';
import {
	convertToInternationalCurrencySystem,
	convertToTwoDecimal,
} from '../../../helper/convertAmount';
import { chartTitleChange } from '../../../helper/yoyUtils';
import { colors } from '../../../theme/color-palette';
import { convertDateFormat } from '../../../helper/convertDate';
import { getPercentageDiff } from '../../../common/components/charts/LineChart/util';
import DefinitionDrawer, {
	DefinitionContext,
} from '../../../components/Definition/index';
import { getChartType } from '../../../components/Definition';

const loaderStyles = {
	loader: {
		display: 'flex',
		height: '424px',
	},
	circularProgress: {
		margin: 'auto',
	},
};

const useStyles = makeStyles(() => ({
	container: {
		width: (P) => P.chartWidth,
		border: `1px solid ${colors.LIGHT_GREY}`,
		borderRadius: '8px',
		height: '430px',
		margin: '8px',
	},
	header: {
		marginLeft: 8,
		marginTop: 8,
		height: '27px',
		display: 'flex',
		justifyContent: 'space-between',
	},
	subheader: {
		textAlign: 'justify',
		marginLeft: 8,
	},
	chart: {
		margin: 'auto',
		marginTop: 62,
	},
	upArrow: {
		width: 20,
		marginBottom: -4,
		color: colors.POSITIVE_GROWTH_COLOR,
	},
	downArrow: {
		width: 20,
		marginBottom: -4,
		color: colors.NEGATIVE_GROWTH_COLOR,
	},
	actions: {
		display: 'flex',
		margin: 0,
	},
	...loaderStyles,
}));

const styles = {
	title: {
		fontSize: 18,
		textAlign: 'left',
		color: colors.FONT_COLOR,
		fontFamily: constants.BOGLE_REGULAR,
		cursor: 'pointer',
	},
	onHandValue: {
		fontSize: 16,
		textAlign: 'left',
		color: colors.FONT_COLOR,
		fontFamily: constants.BOGLE_MEDIUM,
		display: constants.INLINE_BLOCK,
		marginTop: 4,
	},
	onHandYoY: {
		fontSize: 12,
		fontFamily: constants.BOGLE_LIGHT,
		display: constants.INLINE_BLOCK,
	},
	up: {
		color: colors.POSITIVE_GROWTH_COLOR,
	},
	down: {
		color: colors.NEGATIVE_GROWTH_COLOR,
	},
	onHandYoYLabel: {
		marginLeft: 4,
		fontSize: 12,
		fontFamily: constants.BOGLE_LIGHT,
		display: constants.INLINE_BLOCK,
	},
	export: {
		marginTop: '-15%',
	},
};

export function Icon({ movement }) {
	const classes = useStyles();
	return movement === 'up' ? (
		<ArrowUpward className={classes.upArrow} />
	) : (
		<ArrowDownward className={classes.downArrow} />
	);
}

Icon.propTypes = {
	movement: PropTypes.string,
};

// Retail($), Units(#) dropdown options
const options = [constants.RETAIL, constants.UNIT];

function DrillDownTrend({
	chartName,
	weekFilter,
	category,
	data,
	loading,
	type,
	brandName,
	upcNumber,
	selectedTab,
}) {
	const classes = useStyles();
	const [unit, setUnit] = useState(constants.RETAIL);
	const [totalPercent, setTotalPercent] = useState(0);
	const [thisYearTotal, setThisYearTotal] = useState(0);
	const [width, setWidth] = useState(0);

	const movement = totalPercent > 0 ? 'up' : 'down';
	const isUnit = unit === constants.UNIT;

	const initialCxt = {
		isDrawerOpened: false,
		metricType: '',
	};
	const [definition, setDefinition] = useState(initialCxt);

	const cxtValue = React.useMemo(
		() => ({ definition, setDefinition }),
		[definition]
	);

	const openDefinition = () => {
		setDefinition({
			isDrawerOpened: true,
			metricType: getChartType(chartName),
		});
	};

	useEffect(() => {
		const updateSize = debounce(() => {
			setWidth(window.innerHeight + window.innerWidth);
		}, 1500);
		window.addEventListener('resize', updateSize);
		updateSize();
		return () => window.removeEventListener('resize', updateSize);
	}, []);

	useEffect(() => {
		const summary = isUnit ? data.unit?.summary : data.retail?.summary;
		if (summary) {
			setTotalPercent(summary?.yoy);
			const prefix = isUnit ? '' : '$';
			setThisYearTotal(
				`${prefix}${convertToInternationalCurrencySystem(summary?.total)}`
			);
		}
	}, [unit, data]);

	// chart values
	const chartData = isUnit ? data.unit?.data : data.retail?.data;
	const { chartValue, xAxisData } = getChartProps(
		weekFilter,
		chartData,
		isUnit
	);

	if (chartData?.length === 0 && !loading) {
		const errorCode = isUnit ? data.unit.errorCode : data.retail.errorCode;
		const { errorMsg, showTitle } = getErrorScreenParams(type, errorCode);
		return (
			<ErrorScreenMessage
				errorMsg={errorCode === 204 ? constants.DATA_NOT_AVAILABLE : errorMsg}
				chartTitle={chartName}
				showTitleInContent={showTitle}
			/>
		);
	}

	const parseData = (data = []) => {
		if (data !== null) {
			return data?.map((item) => {
				return {
					...item,
					busDt: convertDateFormat(item.busDt),
					week_number: item.wmYrWkNbr,
					excel_diff_perc: getPercentageDiff(item),
					excel_ty_value: item.tyValue,
					excel_ly_value: item.lyValue,
					wmWkNbr: item.wmWkNbr,
				};
			});
		}
		return [];
	};

	return (
		<Fragment>
			{loading ? (
				<Box className={classes.loader}>
					<CircularProgress className={classes.circularProgress} />
				</Box>
			) : (
				<Fragment>
					<Box className={classes.header}>
						<Box>
							{/* Title */}
							<Typography style={styles.title} onClick={openDefinition}>
								{chartName}
							</Typography>
							<Typography style={styles.onHandValue}>
								{thisYearTotal}
							</Typography>
							{/* Icon */}
							<Icon movement={movement} />
							{/* Value */}
							<Typography
								style={styles.onHandYoY}
								color={styles[movement].color}>
								{`${chartTitleChange(convertToTwoDecimal(totalPercent))}`}
							</Typography>
							<Typography style={styles.onHandYoYLabel}>(YoY)</Typography>
						</Box>
						<Box className={classes.actions}>
							{/* Retail / Unit */}
							<Typography>
								<ToggleFilter
									options={options}
									selectedBtn={unit}
									onChange={(value) => setUnit(value)}
								/>
							</Typography>
							<Typography style={styles.export}>
								{/* Export data */}
								<DownloadTrend
									data={parseData(chartData)}
									chartTitle={chartName}
									weekFilter={weekFilter}
									category={category}
									inventory={true}
									selectedBtn={unit}
									brandName={brandName}
									upcNumber={upcNumber}
									selectedFilter={'Omni'}
									selectedTab={selectedTab}
								/>
							</Typography>
						</Box>
					</Box>
					{/* Chart */}
					<Box className={classes.chart} key={width}>
						<LineChart
							chartWidth={'100%'}
							chartData={parseData(chartData)}
							xAxisData={xAxisData}
							chartValue={chartValue}
							chartName={chartName}
							weekFilter={weekFilter}
							showExploreButton={false}
							isAmount={!isUnit}
							showWeekDates={true}
						/>
					</Box>
				</Fragment>
			)}
			<DefinitionContext.Provider value={cxtValue}>
				<DefinitionDrawer
					isOpen={definition.isDrawerOpened}
					metricType={definition.metricType}
					onClose={() => setDefinition(initialCxt)}
				/>
			</DefinitionContext.Provider>
		</Fragment>
	);
}

DrillDownTrend.propTypes = {
	chartName: PropTypes.string,
	data: PropTypes.object,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	category: PropTypes.string,
	loading: PropTypes.bool,
	type: PropTypes.string,
	brandName: PropTypes.string,
	upcNumber: PropTypes.number,
	selectedTab: PropTypes.string,
};

DrillDownTrend.defaultProps = {
	loading: false,
	type: 'item',
};

export default DrillDownTrend;
